import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BookingForm from '../components/BookingForm';
import SaveCardForm from '../components/SaveCardForm';
import ChatWidget from '../components/ChatWidget';
import VideoCallModal from '../components/VideoCallModal';
import ChefTrackingMap from '../components/ChefTrackingMap';
import NotificationBanner from '../components/NotificationBanner';
import CustomerCateringView from '../components/CustomerCateringView';

const CustomerDashboard = ({ user, token }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showVideoCall, setShowVideoCall] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get('/booking');
      setBookings(response.data);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBookingCreated = () => {
    fetchBookings();
    setShowBookingForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="customer-dashboard">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Customer Dashboard</h1>
          <p className="text-gray-600 mt-2">Welcome back, {user.name}!</p>
        </div>
        <div className="flex items-center gap-4">
          <NotificationBanner userId={user.id} />
          <button
            onClick={() => setShowBookingForm(true)}
            className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
            data-testid="new-booking-button"
          >
            + New Booking
          </button>
        </div>
      </div>

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Book a Chef</h2>
              <button
                onClick={() => setShowBookingForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <BookingForm user={user} onSuccess={handleBookingCreated} />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'bookings'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="bookings-tab"
          >
            My Bookings ({bookings.length})
          </button>
          <button
            onClick={() => setActiveTab('catering')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'catering'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🍽️ Catering
          </button>
          <button
            onClick={() => setActiveTab('payment')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'payment'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            data-testid="payment-tab"
          >
            Payment Methods
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'bookings' && (
        <div className="space-y-6" data-testid="bookings-section">
          {bookings.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-600 mb-4">No bookings yet. Start by booking your first chef!</p>
              <button
                onClick={() => setShowBookingForm(true)}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
              >
                Book Now
              </button>
            </div>
          ) : (
            bookings.map((booking) => (
              <div key={booking.id} className="bg-white rounded-lg shadow p-6" data-testid={`booking-${booking.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {booking.meal_type.charAt(0).toUpperCase() + booking.meal_type.slice(1)} for {booking.guest_count} guests
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      📅 {new Date(booking.date).toLocaleDateString()} at {new Date(booking.date).toLocaleTimeString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      📍 {booking.service_city}, {booking.service_state} {booking.service_zip}
                    </p>
                    <p className="text-sm font-medium text-gray-900 mt-2">
                      Total: ${(booking.total_cents / 100).toFixed(2)}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {booking.status}
                  </span>
                </div>

                {/* Communication Actions for Confirmed Bookings */}
                {booking.status === 'confirmed' && (
                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <div className="flex gap-3">
                      <button
                        onClick={() => setSelectedBooking(booking)}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                      >
                        📍 Track Chef
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBooking(booking);
                          setShowVideoCall(true);
                        }}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                      >
                        📹 Video Call
                      </button>
                    </div>
                  </div>
                )}

                {/* Chef Tracking Section */}
                {selectedBooking && selectedBooking.id === booking.id && !showVideoCall && (
                  <div className="mt-6">
                    <ChefTrackingMap bookingId={booking.id} />
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'catering' && (
        <CustomerCateringView userId={user.id} />
      )}

      {activeTab === 'payment' && (
        <div className="bg-white rounded-lg shadow p-6" data-testid="payment-section">
          <h2 className="text-xl font-semibold mb-4">Save Your Card</h2>
          <p className="text-gray-600 mb-6">
            Save a payment method for faster checkout when booking chefs.
          </p>
          <SaveCardForm customerId={user.id} />
        </div>
      )}

      {/* Chat Widget for Active Bookings */}
      {selectedBooking && (
        <ChatWidget
          bookingId={selectedBooking.id}
          userId={user.id}
          userRole="customer"
        />
      )}

      {/* Video Call Modal */}
      {showVideoCall && selectedBooking && (
        <VideoCallModal
          bookingId={selectedBooking.id}
          userId={user.id}
          userRole="customer"
          onClose={() => setShowVideoCall(false)}
        />
      )}
    </div>
  );
};

export default CustomerDashboard;
